import type { MarkdownIt as MarkdownItInstance, StateCore, Token } from 'markdown-it'

export const PROVENANCE_MARKER_CLASS = 'wiki-provenance-marker'

// Ingest and Lint annotate claims with HTML comments such as
// `<!-- src: source-114 2026-08-18 -->` (see Wiki::QualityScorer::PROVENANCE_PATTERN).
// With `html: false` markdown-it escapes them into visible text, so they
// are removed at render time; the files on disk keep them.
const COMMENT_RE = /\s*<!--([\s\S]*?)-->/g
const AMENDMENT_RE = /^\s*(correction|clarified):\s*(\S+)(?:\s+(\d{4}-\d{2}-\d{2}))?/

const AMENDMENT_LABELS: Record<string, string> = {
  correction: 'Corrected by',
  clarified: 'Clarified by',
}

export function installProvenanceRule(md: MarkdownItInstance): void {
  md.core.ruler.after('inline', 'wikiProvenance', (state) => stripProvenance(state, md))
}

function stripProvenance(state: StateCore, md: MarkdownItInstance): boolean {
  for (const blockToken of state.tokens) {
    if (blockToken.type !== 'inline' || !blockToken.children) continue
    const next: Token[] = []
    const children = blockToken.children
    children.forEach((token, idx) => {
      if (token.type === 'text' && token.content.includes('<!--')) {
        splitTextForComments(token, next, state, md, idx === children.length - 1)
      } else {
        next.push(token)
      }
    })
    blockToken.children = next
  }
  state.tokens = dropEmptyParagraphs(state.tokens)
  return false
}

// The regex swallows the whitespace before each comment so an amendment
// chip hugs the claim it annotates; a single space is restored when prose
// continues right after the comment.
function splitTextForComments(
  token: Token,
  out: Token[],
  state: StateCore,
  md: MarkdownItInstance,
  isLast: boolean,
): void {
  const text = token.content
  let cursor = 0
  for (const match of text.matchAll(COMMENT_RE)) {
    pushText(out, state, text.slice(cursor, match.index))
    const chip = amendmentChip(match[1], md)
    if (chip) {
      const html = new state.Token('html_inline', '', 0)
      html.content = chip
      out.push(html)
    }
    cursor = match.index + match[0].length
    if (/^\s/.test(match[0]) && /^[^\s<]/.test(text.slice(cursor))) pushText(out, state, ' ')
  }
  const rest = text.slice(cursor)
  pushText(out, state, isLast && /^\s*$/.test(rest) ? '' : rest)
}

function pushText(out: Token[], state: StateCore, content: string): void {
  if (!content) return
  const text = new state.Token('text', '', 0)
  text.content = content
  out.push(text)
}

function amendmentChip(body: string, md: MarkdownItInstance): string | null {
  const match = AMENDMENT_RE.exec(body)
  if (!match) return null
  const label = [AMENDMENT_LABELS[match[1]], match[2], match[3] ? `on ${match[3]}` : ''].filter(Boolean).join(' ')
  const escaped = md.utils.escapeHtml(label)
  return `<sup class="${PROVENANCE_MARKER_CLASS}" title="${escaped}" aria-label="${escaped}">†</sup>`
}

// A comment alone on its own line leaves an empty paragraph behind.
function dropEmptyParagraphs(tokens: Token[]): Token[] {
  const out: Token[] = []
  for (let i = 0; i < tokens.length; i++) {
    const [open, inline, close] = [tokens[i], tokens[i + 1], tokens[i + 2]]
    const empty =
      open.type === 'paragraph_open' &&
      inline?.type === 'inline' &&
      close?.type === 'paragraph_close' &&
      (inline.children ?? []).length === 0
    if (empty) {
      i += 2
      continue
    }
    out.push(open)
  }
  return out
}
